/**
 * Deterministic TON Connect payloads. Every cell is built by @gong/curve-math
 * so the contract reads the same layout the Mini App signed. No server secrets.
 */
import { Address, Cell } from '@ton/core';
import {
  CurveConfig,
  DEFAULT_CURVE_CONFIG,
  GAS_SELL,
  GAS_CLAIM,
  GAS_HARVEST_LP,
  buildBuyBody,
  buildClaimBody,
  buildHarvestLpBody,
  buildCreateLaunchBody,
  buildOffchainContent,
  buildStonDexParams,
  buildSellBody,
  buyValue,
  createLaunchValue,
  DexKind,
  type StonDexParams,
} from '@gong/curve-math';

import { IS_TESTNET } from '../config';

export interface TonConnectMessage {
  address: string;
  amount: string;
  payload: string;
}

export interface TonConnectTx {
  validUntil: number;
  messages: TonConnectMessage[];
}

function friendly(address: Address): string {
  return address.toString({ bounceable: true, urlSafe: true, testOnly: IS_TESTNET });
}

export function cellToBase64(cell: Cell): string {
  return cell.toBoc().toString('base64');
}

export interface AssembleBuyArgs {
  curve: Address;
  tonIn: bigint;
  minOut: bigint;
  deadline: number;
  referrer?: Address | null;
  queryId?: bigint;
}

export function assembleBuy(args: AssembleBuyArgs): TonConnectTx {
  const body = buildBuyBody({
    queryId: args.queryId,
    minOut: args.minOut,
    deadline: args.deadline,
    referrer: args.referrer ?? null,
  });
  return {
    validUntil: args.deadline,
    messages: [
      {
        address: friendly(args.curve),
        amount: buyValue(args.tonIn).toString(),
        payload: cellToBase64(body),
      },
    ],
  };
}

export interface AssembleSellArgs {
  jettonWallet: Address;
  curve: Address;
  responseDestination: Address;
  jettons: bigint;
  minOut: bigint;
  deadline: number;
  referrer?: Address | null;
  queryId?: bigint;
  forwardTonAmount?: bigint;
}

export function assembleSell(args: AssembleSellArgs): TonConnectTx {
  const body = buildSellBody({
    queryId: args.queryId,
    jettons: args.jettons,
    minOut: args.minOut,
    deadline: args.deadline,
    referrer: args.referrer ?? null,
    curve: args.curve,
    responseDestination: args.responseDestination,
    forwardTonAmount: args.forwardTonAmount,
  });
  return {
    validUntil: args.deadline,
    messages: [
      {
        address: friendly(args.jettonWallet),
        amount: GAS_SELL.toString(),
        payload: cellToBase64(body),
      },
    ],
  };
}

export function assembleClaim(vault: Address, deadline: number): TonConnectTx {
  return {
    validUntil: deadline,
    messages: [{
      address: friendly(vault),
      amount: GAS_CLAIM.toString(),
      payload: cellToBase64(buildClaimBody()),
    }],
  };
}

export function assembleHarvest(locker: Address, deadline: number): TonConnectTx {
  return {
    validUntil: deadline,
    messages: [{ address: friendly(locker), amount: GAS_HARVEST_LP.toString(),
      payload: cellToBase64(buildHarvestLpBody()) }],
  };
}

export interface AssembleCreateLaunchArgs {
  factory: Address;
  salt: bigint;
  contentUri: string;
  channel: Address;
  seedTon: bigint;
  devBuyTon: bigint;
  deadline: number;
  launchFee?: bigint;
  config?: CurveConfig;
  queryId?: bigint;
  stonDexParams?: StonDexParams;
}

/**
 * One TON Connect message: deploy + liquidity seed + optional creator purchase.
 * Seed and dev-buy stay separate coins in the payload (ADR-0006).
 */
export function assembleCreateLaunch(args: AssembleCreateLaunchArgs): TonConnectTx {
  const config = args.config ?? DEFAULT_CURVE_CONFIG;
  const launchFee = args.launchFee ?? config.launchFee;
  const body = buildCreateLaunchBody({
    queryId: args.queryId,
    salt: args.salt,
    content: buildOffchainContent(args.contentUri),
    channel: args.channel,
    seedTon: args.seedTon,
    devBuyTon: args.devBuyTon,
    dexKind: args.stonDexParams ? DexKind.stonfi : 0,
    dexParams: args.stonDexParams ? buildStonDexParams(args.stonDexParams) : null,
  });
  return {
    validUntil: args.deadline,
    messages: [
      {
        address: friendly(args.factory),
        amount: createLaunchValue({
          launchFee,
          seedTon: args.seedTon,
          devBuyTon: args.devBuyTon,
        }).toString(),
        payload: cellToBase64(body),
      },
    ],
  };
}

export function newLaunchSalt(): bigint {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  let salt = 0n;
  for (const byte of bytes) {
    salt = (salt << 8n) | BigInt(byte);
  }
  return salt;
}
